import { TourEvent } from "@common/types/traclink/tour-event";
import TracPage from "@services/tracpage/types/trac-page";
import { SectionIdentifier } from "@services/tracpage/types/trac-page-section-id";
import { MerchProduct } from "@services/merch/types/api/merch-product";
import { getSortEventsFn, getSortMerchFn, getSortMusicFn } from "./items-sort";

type GroupedByVisibility<T> = {
  visible: T[];
  hidden: T[];
};

export const groupMusicByVisibility = (pageData: TracPage) => {
  const releases = pageData.links.filter(
    (link) => link.type !== "url" && !!link.id
  );
  const sectionConfig = pageData.itemsConfig?.[SectionIdentifier.MUSIC];

  return groupByVisibility(
    releases,
    (r) => sectionConfig?.[r.id ?? ""]?.isHidden ?? false,
    getSortMusicFn(pageData)
  );
};

export const groupEventsByVisibility = (
  events: TourEvent[],
  pageData: TracPage
) => {
  const sectionConfig = pageData.itemsConfig?.[SectionIdentifier.EVENTS];

  return groupByVisibility(
    events,
    ({ id }) => sectionConfig?.[id]?.isHidden ?? false,
    getSortEventsFn(pageData)
  );
};

export const groupMerchByVisibility = (
  merch: MerchProduct[],
  pageData: TracPage
) => {
  const filtered = merch.filter((m) => !m.status || m.status === "published");
  const sectionConfig = pageData.itemsConfig?.[SectionIdentifier.MERCH];

  return groupByVisibility(
    filtered,
    ({ id }) => sectionConfig?.[id]?.isHidden ?? false,
    getSortMerchFn(pageData)
  );
};

const groupByVisibility = <T>(
  data: T[],
  isHiddenExtractor: (el: T) => boolean,
  sortFn?: (a: T, b: T) => number
): GroupedByVisibility<T> => {
  const grouped = data.reduce<GroupedByVisibility<T>>(
    (acc, curr) => {
      const isHidden = isHiddenExtractor(curr);
      if (isHidden) {
        acc.hidden = [...acc.hidden, curr];
      } else {
        acc.visible = [...acc.visible, curr];
      }

      return acc;
    },
    { visible: [], hidden: [] }
  );

  if (!sortFn) {
    return grouped;
  }

  return Object.fromEntries(
    Object.entries(grouped).map(([key, arr]) => [key, arr.sort(sortFn)])
  ) as GroupedByVisibility<T>;
};
